import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import axiosInstance from '../../../shared/api/axiosInstance';
import type { PaymentTermsBasis } from '../../../shared/payment-terms';
import { useGetOperationalDefaultsApiV1AccountingSettingsOperationalDefaultsGet } from '../../../generated/api/accounting-settings/accounting-settings';
import type { OperationalDefaults } from '../../../generated/api/models/operationalDefaults';
import type { RevRecBasis } from '../../../generated/api/models/revRecBasis';
import type { VatRegistration } from '../../../generated/api/models/vatRegistration';
// VAT enums single-sourced from shared/config/{vat_filing_frequency,vat_scheme}.config.json
// → apps/frontend/src/generated/config-enums.ts (config_enums_generator.py).
import type { VatFilingFrequency, VatScheme } from '../../../generated/config-enums';
// Card brand / funding value sets single-sourced from shared/config/{card_brand,card_funding}.config.json.
import type { CardBrand, CardFunding } from '../../../generated/config-enums';
// e-Invoicing format value set single-sourced from shared/config/einvoice_format.config.json.
import type { EInvoiceFormat } from '../../../generated/config-enums';
// Auto-invoicing cadence value set single-sourced from
// shared/config/accounting_billing_frequency.config.json.
import type { AccountingBillingFrequency } from '../../../generated/config-enums';

export type { VatRegistration };
export type { VatFilingFrequency, VatScheme };
export type { CardBrand, CardFunding };
export type { EInvoiceFormat };
export type { AccountingBillingFrequency };

// OC&CO C2 — one tenant-configured credit-card surcharge rule. A null brand /
// issuer_country / funding is a wildcard (matches any). Stored inside the
// `card_surcharge_rules` JSONB on accounting_tenant_settings; the value set is
// validated at the settings service seam. `surcharge_percent` is persisted as a
// stringified DECIMAL, so reads can be a string — the editor coerces to a number.
export interface CardSurchargeRule {
  brand: CardBrand | null;
  issuer_country: string | null;
  funding: CardFunding | null;
  surcharge_percent: number | string;
  label: string | null;
}

export interface AccountingTenantSettings {
  id: string | null;
  tenant_id: string | null;
  // Invoice numbering
  invoice_format_pattern: string;
  invoice_start_sequence_from: number;
  invoice_seq_reset: string;
  // Payment numbering
  payment_format_pattern: string;
  payment_start_sequence_from: number;
  payment_seq_reset: string;
  // Payment received numbering
  payment_received_format_pattern: string;
  payment_received_start_sequence_from: number;
  payment_received_seq_reset: string;
  // Bill numbering
  bill_format_pattern: string;
  bill_start_sequence_from: number;
  bill_seq_reset: string;
  // Payment-run numbering (T17)
  payment_run_format_pattern: string;
  payment_run_start_sequence_from: number;
  payment_run_seq_reset: string;
  // T19 — Bank deposit / transfer numbering + per-method routing
  bank_deposit_format_pattern: string;
  bank_deposit_start_sequence_from: number;
  bank_deposit_seq_reset: string;
  bank_transfer_format_pattern: string;
  bank_transfer_start_sequence_from: number;
  bank_transfer_seq_reset: string;
  /** JSONB: { default_clearing_account_id, method_to_bank_account: { ach, wire, card, digital_wallet } } */
  deposit_routing: {
    default_clearing_account_id?: string | null;
    method_to_bank_account?: Record<string, string | null>;
  } | null;
  // Organization branding (company info moved to tenant settings)
  logo_file_id: string | null;
  // Expense-account granularity (bill-import suggestion bias).
  expense_account_detail_level: string;
  // Defaults
  fiscal_year_start_month: number;
  /** Normalized default payment-terms value object (ERP P3). */
  default_payment_terms_id: string | null;
  default_payment_terms_basis: PaymentTermsBasis | null;
  default_payment_terms_days: number | null;
  default_sales_account_id: string | null;
  default_purchase_account_id: string | null;
  default_tax_rate_id: string | null;
  // Income account a sales-return restocking fee posts to (finding [167]).
  restocking_fee_account_id: string | null;
  // Features
  auto_generate_journal_entries: boolean;
  // AP payment-run approval controls (WI a9901d1c). Threshold is nullable —
  // null means "require approval for every run". auto_approve_enabled is the
  // dual-control kill-switch; window_hours drives the anti-structuring guard.
  ap_approval_required_above_amount: number | null;
  ap_auto_approve_enabled: boolean;
  ap_auto_approve_window_hours: number;
  /**
   * Whether a tenant-wide statutory EIN is on file — never the number itself.
   * The EIN is stored encrypted (`company_tax_id_encrypted`) and is the NACHA
   * originator / 1099 filer id; the plaintext comes only from
   * `useRevealCompanyTaxId()`.
   */
  company_tax_id_set: boolean;
  // Israeli tax compliance
  il_osek_morsheh: string | null;
  il_company_registration: string | null;
  il_nikuyim_file_number: string | null;
  il_tax_registration_type: string | null;
  // VAT registration — the tenant's own VAT registration(s) + filing config
  vat_home_country: string | null;
  vat_home_number: string | null;
  vat_registrations: VatRegistration[];
  vat_filing_frequency: VatFilingFrequency | null;
  vat_scheme: VatScheme;
  // e-Invoicing outbound channel (UBL 2.1 / PEPPOL BIS). OFF by default; the
  // seller PEPPOL endpoint has no home outside this settings surface.
  einvoicing_enabled: boolean;
  einvoice_format: EInvoiceFormat;
  einvoice_endpoint_id: string | null;
  einvoice_endpoint_scheme_id: string | null;
  // Procurement defaults
  default_claim_expense_account_id: string | null;
  // Deferred-revenue liability account (feature gap [33]); null = engine off
  deferred_revenue_account_id: string | null;
  // Well-known GL role overrides edited by GlAccountMappingsTab (WI 49bf39c9).
  // null = no override, so the posting service resolves from the chart instead.
  default_ar_account_id: string | null;
  customer_deposit_account_id: string | null;
  default_customer_discount_account_id: string | null;
  default_vendor_prepayment_account_id: string | null;
  default_discount_account_id: string | null;
  finance_charge_income_account_id: string | null;
  nsf_fee_income_account_id: string | null;
  // Freight (TMS B5/B6) — persona-gated (sells_freight_services)
  freight_revenue_recognition_basis: RevRecBasis;
  freight_accrued_cost_account_id: string | null;
  freight_accrued_revenue_account_id: string | null;
  freight_accrual_auto_post: boolean;
  // Credit control (G2) — tenant opt-in; default off (no surprise blocking)
  credit_control_enabled: boolean;
  // Auto-invoicing policy — the ORG-WIDE defaults every customer inherits unless
  // it states an opinion of its own. NOT NULL columns, so always present.
  // `default_billing_frequency` is the INVOICING cadence, not
  // `default_statement_frequency` (how often a statement of the account goes out);
  // `default_consolidate_invoices` is one invoice per period, not the
  // statement-side `combine_invoices_on_statement`.
  auto_invoicing_enabled: boolean;
  default_billing_frequency: AccountingBillingFrequency;
  default_consolidate_invoices: boolean;
  auto_invoice_delay_hours: number;
  // P2P — AP members appointed to issue outbound payments (Payments Due nags
  // target these users; empty falls back to accounting admins)
  auto_invoicing_notify_user_ids: string[];
  outbound_payment_user_ids: string[];
  // ...and the DEPARTMENT spelling of that roster: appointed user groups, expanded
  // through their sub-groups on the backend. Unions with the users above.
  outbound_payment_group_ids: string[];
  // Days ahead of due an item counts as "due soon" in that reminder. null = the
  // rule's own default (3); 0 = overdue only.
  outbound_payment_lead_days: number | null;
  // OC&CO C2 — portal self-service payment: card surcharge rules engine + the
  // bank details shown for the wire option.
  card_surcharge_enabled: boolean;
  card_surcharge_credit_only: boolean;
  card_surcharge_rules: CardSurchargeRule[];
  card_surcharge_income_account_id: string | null;
  portal_wire_bank_account_id: string | null;
  portal_wire_instructions: string | null;
  settings: Record<string, unknown>;
  updated_by: string | null;
}

interface TenantSettingsUpdate {
  invoice_format_pattern?: string;
  invoice_start_sequence_from?: number;
  invoice_seq_reset?: string;
  payment_format_pattern?: string;
  payment_start_sequence_from?: number;
  payment_seq_reset?: string;
  payment_received_format_pattern?: string;
  payment_received_start_sequence_from?: number;
  payment_received_seq_reset?: string;
  bill_format_pattern?: string;
  bill_start_sequence_from?: number;
  bill_seq_reset?: string;
  payment_run_format_pattern?: string;
  payment_run_start_sequence_from?: number;
  payment_run_seq_reset?: string;
  bank_deposit_format_pattern?: string;
  bank_deposit_start_sequence_from?: number;
  bank_deposit_seq_reset?: string;
  bank_transfer_format_pattern?: string;
  bank_transfer_start_sequence_from?: number;
  bank_transfer_seq_reset?: string;
  deposit_routing?: {
    default_clearing_account_id?: string | null;
    method_to_bank_account?: Record<string, string | null>;
  };
  logo_file_id?: string;
  fiscal_year_start_month?: number;
  default_payment_terms_id?: string | null;
  default_payment_terms_basis?: PaymentTermsBasis | null;
  default_payment_terms_days?: number | null;
  default_sales_account_id?: string;
  default_purchase_account_id?: string;
  deferred_revenue_account_id?: string | null;
  default_tax_rate_id?: string;
  restocking_fee_account_id?: string | null;
  auto_generate_journal_entries?: boolean;
  // AP payment-run approval controls (WI a9901d1c). Threshold nullable — null
  // clears it (require approval for every run).
  ap_approval_required_above_amount?: number | null;
  ap_auto_approve_enabled?: boolean;
  ap_auto_approve_window_hours?: number;
  /** Statutory EIN in clear — stored encrypted. Null/blank clears it. */
  company_tax_id?: string | null;
  il_osek_morsheh?: string;
  il_company_registration?: string;
  il_nikuyim_file_number?: string;
  il_tax_registration_type?: string;
  // VAT registration (home country/number nullable — null clears to NULL)
  vat_home_country?: string | null;
  vat_home_number?: string | null;
  vat_registrations?: VatRegistration[];
  vat_filing_frequency?: VatFilingFrequency;
  vat_scheme?: VatScheme;
  // e-Invoicing (endpoint id/scheme nullable — null clears to NULL)
  einvoicing_enabled?: boolean;
  einvoice_format?: EInvoiceFormat;
  einvoice_endpoint_id?: string | null;
  einvoice_endpoint_scheme_id?: string | null;
  default_claim_expense_account_id?: string | null;
  // Well-known GL role overrides (WI 49bf39c9) — all nullable; null clears the
  // override and returns the role to chart resolution. The backend rejects an
  // account of the wrong type / another tenant with VALIDATION_ERROR.
  default_ar_account_id?: string | null;
  customer_deposit_account_id?: string | null;
  default_customer_discount_account_id?: string | null;
  default_vendor_prepayment_account_id?: string | null;
  default_discount_account_id?: string | null;
  finance_charge_income_account_id?: string | null;
  nsf_fee_income_account_id?: string | null;
  // Freight (TMS B5/B6)
  freight_revenue_recognition_basis?: RevRecBasis;
  freight_accrued_cost_account_id?: string | null;
  freight_accrued_revenue_account_id?: string | null;
  freight_accrual_auto_post?: boolean;
  // Credit control (G2)
  credit_control_enabled?: boolean;
  // Auto-invoicing policy (tenant tier). Omitted = leave as-is; these back NOT
  // NULL columns, so there is no null-to-clear arm.
  auto_invoicing_enabled?: boolean;
  default_billing_frequency?: AccountingBillingFrequency;
  default_consolidate_invoices?: boolean;
  auto_invoice_delay_hours?: number;
  // P2P — designated outbound-payment users
  auto_invoicing_notify_user_ids?: string[];
  outbound_payment_user_ids?: string[];
  outbound_payment_group_ids?: string[];
  outbound_payment_lead_days?: number | null;
  // OC&CO C2 — portal card surcharge + wire settings (account/bank/instructions
  // nullable — null clears to NULL).
  card_surcharge_enabled?: boolean;
  card_surcharge_credit_only?: boolean;
  card_surcharge_rules?: CardSurchargeRule[];
  card_surcharge_income_account_id?: string | null;
  portal_wire_bank_account_id?: string | null;
  portal_wire_instructions?: string | null;
}

export const accountingSettingsKeys = {
  all: ['accounting-settings'] as const,
  tenant: () => [...accountingSettingsKeys.all, 'tenant'] as const,
};

export function useAccountingTenantSettings(options?: { enabled?: boolean }) {
  return useQuery({
    queryKey: accountingSettingsKeys.tenant(),
    queryFn: async () => {
      const response = await axiosInstance.get<{ data: AccountingTenantSettings | null }>(
        '/api/v1/accounting/settings/tenant'
      );
      return response.data.data;
    },
    // The tenant-settings read is module-admin only; gate it (e.g. on admin) so a
    // non-admin viewer doesn't fire a guaranteed-403 request on every page.
    enabled: options?.enabled ?? true,
  });
}

/**
 * The four operational defaults an ORDINARY accounting member needs to do the
 * work the module asks of them — seeding an invoice line's revenue account,
 * labelling a report's fiscal year, and telling a payment run's reader whether
 * the run needs approval.
 *
 * `useAccountingTenantSettings` above is `require_module_admin("accounting")` on
 * the READ, so a plain member's request 403s and `data` stays `undefined` — which
 * every consumer then rendered as ordinary emptiness (no seeded sales account, a
 * January fiscal year, an auto-approving payment run). This read is gated on
 * module ACCESS, so the member gets the real answer. It is deliberately NOT a
 * subset view of the admin settings model: the payload enumerates exactly four
 * fields, so widening the tenant settings later cannot silently widen this one.
 *
 * `fiscal_year_start_month` and `ap_auto_approve_enabled` are REQUIRED and
 * non-null on the payload — never re-introduce a `?? 1` / `?? true` fallback,
 * which is the confident-wrong-answer this route exists to end. Handle the
 * in-flight window honestly instead.
 */
export function useAccountingOperationalDefaults() {
  return useGetOperationalDefaultsApiV1AccountingSettingsOperationalDefaultsGet<OperationalDefaults>(
    { query: { select: (response) => response.data } }
  );
}

export function useUpdateAccountingTenantSettings() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async (data: TenantSettingsUpdate) => {
      const response = await axiosInstance.put<{ data: AccountingTenantSettings }>(
        '/api/v1/accounting/settings/tenant',
        data
      );
      return response.data.data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: accountingSettingsKeys.tenant() });
      // A tenant-level role change can close (or open) a coverage gap on any
      // company that runs its own chart.
      queryClient.invalidateQueries({ queryKey: glMappingsCoverageKeys.all });
    },
  });
}

/**
 * Fetch the tenant's company tax ID (EIN) in clear, for the reveal control on
 * Company Details.
 *
 * Kept off the settings payload deliberately: the number is a stored secret, so
 * it is fetched only when someone asks to see it. Disabled until then, and never
 * persisted to the offline cache.
 */
export function useRevealCompanyTaxId(enabled: boolean) {
  return useQuery({
    queryKey: [...accountingSettingsKeys.tenant(), 'company-tax-id'] as const,
    queryFn: async () => {
      const response = await axiosInstance.get<{ data: { company_tax_id: string | null } }>(
        '/api/v1/accounting/settings/company-tax-id'
      );
      return response.data.data.company_tax_id;
    },
    enabled,
    gcTime: 0,
    meta: { persist: false },
  });
}

// ---------------------------------------------------------------------------
// Per-company GL account-role mappings
// ---------------------------------------------------------------------------

/**
 * One company's GL role overrides plus the tenant-level fallback for every
 * well-known role. An absent role in `mappings` resolves to `tenant_defaults`,
 * then to the chart of accounts — the same cascade the posting services use
 * (`modules/accounting/services/gl_account_role_service.py`).
 */
export type GlRoleField =
  | 'default_ar_account_id'
  | 'customer_deposit_account_id'
  | 'default_customer_discount_account_id'
  | 'default_vendor_prepayment_account_id'
  | 'default_discount_account_id'
  | 'finance_charge_income_account_id'
  | 'nsf_fee_income_account_id';

export interface CompanyGlMappings {
  company_id: string;
  /** Only roles with a per-company override are present. */
  mappings: Partial<Record<GlRoleField, string>>;
  /** The tenant-level fallback for every role (null when unset). */
  tenant_defaults: Partial<Record<GlRoleField, string | null>>;
}

export const companyGlMappingsKeys = {
  all: ['accounting-company-gl-mappings'] as const,
  byCompany: (companyId: string | undefined) =>
    [...companyGlMappingsKeys.all, companyId ?? 'active'] as const,
};

export function useCompanyGlMappings(companyId?: string, options?: { enabled?: boolean }) {
  return useQuery({
    queryKey: companyGlMappingsKeys.byCompany(companyId),
    queryFn: async () => {
      const response = await axiosInstance.get<{ data: CompanyGlMappings }>(
        '/api/v1/accounting/settings/company-gl-mappings',
        { params: companyId ? { company_id: companyId } : undefined }
      );
      return response.data.data;
    },
    enabled: options?.enabled ?? true,
  });
}

export function useUpdateCompanyGlMappings() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async (vars: {
      companyId: string;
      // A role id sets the override; null clears it (back to the tenant default).
      mappings: Partial<Record<GlRoleField, string | null>>;
    }) => {
      const response = await axiosInstance.put<{ data: CompanyGlMappings }>(
        '/api/v1/accounting/settings/company-gl-mappings',
        { mappings: vars.mappings },
        { params: { company_id: vars.companyId } }
      );
      return response.data.data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: companyGlMappingsKeys.all });
      queryClient.invalidateQueries({ queryKey: glMappingsCoverageKeys.all });
    },
  });
}

/**
 * One (company, GL role) pair that IS configured — a per-company override or the
 * tenant-level column — but resolves to no account for that company, because the
 * account belongs to a chart the company doesn't use. The posting service falls
 * back to chart resolution and, when that finds nothing, silently posts nothing:
 * this is what makes that state visible. Backend seam:
 * `modules/accounting/services/gl_role_coverage_service.py`.
 */
export interface GlRoleCoverageGap {
  company_id: string;
  company_name: string;
  /**
   * A key of the backend `GL_ACCOUNT_ROLES` registry — deliberately wider than
   * `GlRoleField`, which covers only the 7 roles this tab edits. The audit
   * reports all 15, including ones configured on other settings tabs (freight
   * accrual, deferred revenue), and `glAccountMappings.roleNames.*` names them all.
   */
  role: string;
  /** Where the unresolvable value came from. */
  configured_source: 'tenant' | 'company';
  configured_account_id: string;
  configured_account_number: string | null;
  /** The same-numbered account in this company's own chart, when there is one. */
  suggested_account_id: string | null;
  suggested_account_number: string | null;
}

export const glMappingsCoverageKeys = {
  all: ['accounting-gl-mappings-coverage'] as const,
};

/** Every unresolvable configured role, across every company of the tenant. */
export function useGlMappingsCoverage() {
  return useQuery({
    queryKey: glMappingsCoverageKeys.all,
    queryFn: async () => {
      const response = await axiosInstance.get<{ data: GlRoleCoverageGap[] }>(
        '/api/v1/accounting/settings/gl-mappings-coverage'
      );
      return response.data.data;
    },
  });
}
